/* eslint-disable @typescript-eslint/no-explicit-any */

import { jwtDecode } from "jwt-decode";

type TRequestMethods = "GET" | "POST" | "PUT" | "DELETE";

type ISearchParams = {
  [key: string]: any;
  order_by?: Record<string, "asc" | "desc">;
};

export interface IRequestParams {
  method?: TRequestMethods;
  headers?: Record<string, string>;
  searchParams?: ISearchParams;
  data?: FormData | Record<string, any> | string | any[];
  authorization?: boolean;
  rawResponse?: boolean;
}

export interface ICancelablePromise<T> extends Promise<T> {
  cancel: () => void;
}

type APISegment = "root" | "business" | "customer";

export class FunjoinerRestAPI {
  auth: FunjoinerAuth;
  constructor(
    private base: string,
    apiSegment: APISegment,
    private defaultHeaders: Record<string, string> = {},
  ) {
    this.auth = new FunjoinerAuth(base, apiSegment, this.makeApiRequest);
  }
  onError = (cb: (e: Error) => void) => {
    this.errorHandlers.push(cb);
    return () => {
      this.errorHandlers = this.errorHandlers.filter((f) => f !== cb);
    };
  };
  get = <T = any>(
    path: string,
    params?: Omit<IRequestParams, "method" | "data">,
  ): ICancelablePromise<T> =>
    this.makeApiRequest(path, { ...params, method: "GET" });
  post = <T = any>(
    path: string,
    params?: Omit<IRequestParams, "method">,
  ): ICancelablePromise<T> =>
    this.makeApiRequest(path, { ...params, method: "POST" });
  put = <T = any>(
    path: string,
    params?: Omit<IRequestParams, "method">,
  ): ICancelablePromise<T> =>
    this.makeApiRequest(path, { ...params, method: "PUT" });
  delete = <T = any>(
    path: string,
    params?: Omit<IRequestParams, "method">,
  ): ICancelablePromise<T> =>
    this.makeApiRequest(path, { ...params, method: "DELETE" });

  private makeApiRequest = (path: string, params: IRequestParams = {}) => {
    const requiresAuth = params.authorization ?? true;
    const method = params.method ?? "GET";
    const url = this.buildUrl(path, params);
    const body = this.buildBody(params);
    const _headers = this.buildHeaders(params);

    const controller = new AbortController();
    const signal = controller.signal;
    const stackTrace = new Error("").stack;

    const result = new Promise((resolve, reject) => {
      const rejectWithStack = (err: Error) => {
        err.stack += "\n" + stackTrace;
        reject(err);
      };
      const execute = async (
        isRetryAfter401: boolean = false,
      ): Promise<Response | void> => {
        const headers = { ..._headers };
        if (requiresAuth) {
          const token = await this.auth.getAccessToken({
            forceRefresh: isRetryAfter401,
          });
          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          }
        }
        return fetch(url, {
          method,
          headers,
          body,
          signal,
        })
          .then((res: Response) => {
            if (res.status === 401 && requiresAuth && !isRetryAfter401) {
              return execute(true);
            }

            if (params.rawResponse) return res;

            if (res.status === 500)
              throw new ApplicationError(
                "Server error",
                ERROR_CODES.SERVER_ERROR,
              );
            if (res.status === 429) {
              throw new ApplicationError(
                "Too many requests. Please try again in a moment",
                ERROR_CODES.TOO_MANY_REQUESTS,
              );
            }
            if (res.status === 401) {
              throw new ApplicationError(
                "Unauthorized",
                ERROR_CODES.NOT_AUTHORIZED,
              );
            }
            return res.json().then((data: APIResult) => {
              if (data.ok) {
                return data.result;
              } else {
                if (data.error.type === "DomainException") {
                  throw new DomainError(
                    data.error.message,
                    data.error.code,
                    data.error.payload,
                  );
                } else if (data.error.type === "ApplicationException") {
                  throw new ApplicationError(
                    data.error.message,
                    data.error.code,
                    data.error.payload,
                  );
                } else if (data.error.type === "ValidationException") {
                  throw new ValidationError(
                    data.error.message,
                    data.error.errors,
                  );
                }
                throw new Error(data.error?.message ?? "Unknown error");
              }
            });
          })
          .then((response) => resolve(response))
          .catch((err) => {
            for (const handler of this.errorHandlers) {
              try {
                handler(err);
              } catch (e) {
                err.stack +=
                  "\nAnother exception occurred during handling the above exception:\n" +
                  (e instanceof Error ? e.stack : e);
                break;
              }
            }
            rejectWithStack(err);
          });
      };
      return execute();
    });
    (result as any).cancel = () => controller.abort();
    return result as ICancelablePromise<any>;
  };

  private buildUrl(path: string, params: IRequestParams) {
    function buildOrderBy(orderByDict: Record<string, "asc" | "desc">) {
      const pairs = Object.entries(orderByDict)
        .filter(([_oK, _oV]) => _oV != null)
        .map(([_oK, _oV]) => [_oK, String(_oV)]);
      return pairs.map((pair) => pair.join(":")).join(",");
    }

    const _p = Object.entries(params.searchParams ?? [])
      .filter(([_, v]) => !isEmpty(v))
      .map(([k, v]) => [
        k,
        k === "order_by" && typeof v !== "string"
          ? buildOrderBy(v)
          : v.toString(),
      ]);
    const _path = path + "?" + new URLSearchParams(_p).toString();
    return constructUrl(this.base, _path);
  }

  private buildHeaders(params: IRequestParams) {
    let headers: Record<string, string> = {};
    // Provide prefilled values
    if (!(params.data instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }
    headers = { ...this.defaultHeaders, ...headers, ...params.headers };
    return headers;
  }

  private buildBody(params: IRequestParams) {
    if (params.data == null || params.method === "GET") return undefined;
    if (params.data instanceof FormData) return params.data;
    return JSON.stringify(params.data);
  }

  private errorHandlers: ((e: Error) => void)[] = [];
}
const isEmpty = (obj?: object) =>
  obj == null ||
  (Array.isArray(obj) && obj.length === 0) ||
  (Object.keys(obj).length === 0 &&
    Object.getPrototypeOf(obj) === Object.prototype);

type APIResult =
  | { ok: true; result: unknown }
  | {
      ok: false;
      error:
        | {
            type: "DomainException";
            code: string;
            message: string;
            payload?: any;
          }
        | {
            type: "ApplicationException";
            code: string;
            message: string;
            payload?: any;
          }
        | {
            type: "ValidationException";
            errors: string[];
            message: string;
            payload?: any;
          }
        | { type?: undefined; message?: string; payload?: any };
    };

export const ERROR_CODES = {
  TOO_MANY_REQUESTS: "TOO_MANY_REQUESTS",
  SERVER_ERROR: "SERVER_ERROR",
  NOT_AUTHORIZED: "NOT_AUTHORIZED",
  NO_SUCH_COMPANY: "NO_SUCH_COMPANY",
  COMPANY_LOCKED: "COMPANY_LOCKED",
} as const;

export class DomainError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly payload?: any,
  ) {
    super(message);
    this.name = "DomainError";
  }
}

export class ApplicationError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly payload?: any,
  ) {
    super(message);
    this.name = "ApplicationError";
  }
}

interface ITokenPair {
  access_token: string;
  refresh_token: string;
}

interface IDecodedToken {
  claims: string;
  is_restricted: boolean;
}

type AuthStateChangedCb = (n: IDecodedToken | null) => void;

export class FunjoinerAuth {
  private tokenStorage: TokenStorage;
  public readonly initPromise: Promise<void>;

  // Synchronizes auth state between tabs and windows
  private authBroadcastChannel: BroadcastChannel;

  constructor(
    private base: string,
    private apiSegment: APISegment,
    private makeApiRequest: FunjoinerRestAPI["makeApiRequest"],
  ) {
    this._authStateCallbacks.push((token) => {
      this._currentAuthState = token;
    });

    this.authBroadcastChannel = new BroadcastChannel(`${apiSegment}_auth`);
    this.authBroadcastChannel.onmessage = (
      event: MessageEvent<IDecodedToken | null>,
    ) => {
      this._notifyCurrentTabListenersTokenChanged(event.data);
    };

    this.tokenStorage = new TokenStorage(apiSegment);
    this.initPromise = this.init();
  }

  async init() {
    const token = await this.getAccessToken();
    if (token != null) this._notifyListenersTokenChanged(jwtDecode(token));
  }

  onAuthStateChanged(cb: AuthStateChangedCb) {
    this._authStateCallbacks.push(cb);
    cb(this._currentAuthState);
    return () =>
      (this._authStateCallbacks = this._authStateCallbacks.filter(
        (f) => f !== cb,
      ));
  }

  logout = async () => {
    const access_token = await this.getAccessToken({ forceRefresh: true });
    this.tokenStorage.clearTokenPair();
    this._notifyListenersTokenChanged(null);
    if (access_token != null) {
      return this.makeApiRequest(this.urls.logout, {
        method: "POST",
        authorization: false,
        headers: {
          Authorization: "Bearer " + access_token,
        },
      }).catch((e) => console.error(e));
    }
  };

  getAccessToken = async (
    params: { forceRefresh?: boolean } = { forceRefresh: false },
  ) => {
    if (this._tokenPromise != null) return await this._tokenPromise;
    this._tokenPromise = this._getAccessToken(params.forceRefresh);
    try {
      return await this._tokenPromise;
    } finally {
      this._tokenPromise = null;
    }
  };

  async signInWithEmailAndPassword(email: string, password: string) {
    const pair: ITokenPair = await this.makeApiRequest(this.urls.email_pass, {
      authorization: false,
      method: "POST",
      data: { email, password },
    });

    this.tokenStorage.saveTokenPair(pair);
    this._notifyListenersTokenChanged(jwtDecode(pair.access_token));
  }

  async signInWithFirebaseToken(token: string) {
    const pair: ITokenPair = await this.makeApiRequest(
      this.urls.firebase_token,
      {
        authorization: false,
        method: "POST",
        data: { token },
      },
    );

    this.tokenStorage.saveTokenPair(pair);
    this._notifyListenersTokenChanged(jwtDecode(pair.access_token));
  }

  async getEmailCode(email: string) {
    const codeId: string = await this.makeApiRequest(this.urls.get_email_code, {
      authorization: false,
      method: "POST",
      data: { email },
    });
    return codeId;
  }

  async continueWithEmailCode(codeId: string, code: string) {
    const pair: ITokenPair = await this.makeApiRequest(
      this.urls.continue_with_email_code,
      {
        authorization: false,
        method: "POST",
        data: { code_id: codeId, code },
      },
    );

    this.tokenStorage.saveTokenPair(pair);
    this._notifyListenersTokenChanged(jwtDecode(pair.access_token));
  }

  private get urls() {
    const apiPrefix = {
      root: "root_api",
      business: "api",
      customer: "customer_api",
    }[this.apiSegment];
    return {
      email_pass: constructUrl(
        this.base,
        `/${apiPrefix}/v2/auth/email_password`,
      ),
      get_email_code: constructUrl(
        this.base,
        `/${apiPrefix}/v2/auth/get_email_code`,
      ),
      continue_with_email_code: constructUrl(
        this.base,
        `/${apiPrefix}/v2/auth/continue_with_email_code`,
      ),
      firebase_token: constructUrl(
        this.base,
        `/${apiPrefix}/v2/auth/firebase_token`,
      ),
      refresh: constructUrl(this.base, `/${apiPrefix}/v2/auth/refresh`),
      logout: constructUrl(this.base, `/${apiPrefix}/v2/auth/logout`),
    };
  }

  private _tokenPromise: Promise<string | undefined> | null = null;

  private _getAccessToken = async (forceRefresh = false) => {
    const token = forceRefresh ? undefined : this._getFromStorage();
    if (token != null) return token;
    const refresh_token = this.tokenStorage.refreshToken;
    if (refresh_token == null) {
      if (this._currentAuthState != null) {
        this.tokenStorage.clearTokenPair();
        this._notifyListenersTokenChanged(null);
      }
      return;
    }

    try {
      const pair: ITokenPair = await this.makeApiRequest(this.urls.refresh, {
        authorization: false,
        method: "POST",
        data: { refresh_token },
      });
      this.tokenStorage.saveTokenPair(pair);
      return pair.access_token;
    } catch (e) {
      if (isApplicationError(e) && e.code === "NOT_AUTHORIZED") {
        this.tokenStorage.clearTokenPair();
        this._notifyListenersTokenChanged(null);
      } else {
        throw e;
      }
    }
  };

  private _getFromStorage() {
    const token = this.tokenStorage.accessToken;
    if (token == null) return;
    try {
      const { exp } = jwtDecode<{ exp: number }>(token);
      if (exp <= Date.now() / 1000) return;
    } catch (e) {
      return;
    }
    return token;
  }

  private _notifyListenersTokenChanged(token: IDecodedToken | null) {
    // BroadcastChannel.onmessage is not run for the sender,
    // that's why we should notify current tab listeners ourselves
    this._notifyCurrentTabListenersTokenChanged(token);

    // Post message is delayed, because it's not guaranteed that
    // localStorage has enough time to update before the message is sent
    //
    // So, despite setting token before postMessage, other tab may not
    // have the token yet https://github.com/whatwg/html/issues/403
    setTimeout(
      () => this.authBroadcastChannel.postMessage(token),
      500, // just to make sure
    );
  }

  private _notifyCurrentTabListenersTokenChanged(token: IDecodedToken | null) {
    if (
      token?.claims !== this._currentAuthState?.claims ||
      // undefined should equal to false. can remove later whem all customers get new token (30 days)
      Boolean(token?.is_restricted) !==
        Boolean(this._currentAuthState?.is_restricted)
    ) {
      this._authStateCallbacks.forEach((f) => f(token));
    }
  }

  private _currentAuthState: IDecodedToken | null = null;

  private _authStateCallbacks: AuthStateChangedCb[] = [];
}

class TokenStorage {
  constructor(private storageName: string = "") {}

  get accessToken() {
    return localStorage.getItem(this.accessTokenStorageKey);
  }

  get refreshToken() {
    return localStorage.getItem(this.refreshTokenStorageKey);
  }

  saveTokenPair(pair: ITokenPair) {
    localStorage.setItem(this.refreshTokenStorageKey, pair.refresh_token);
    localStorage.setItem(this.accessTokenStorageKey, pair.access_token);
  }

  clearTokenPair() {
    localStorage.removeItem(this.accessTokenStorageKey);
    localStorage.removeItem(this.refreshTokenStorageKey);
  }

  private get accessTokenStorageKey() {
    return this.storageName + "_fj-ak";
  }
  private get refreshTokenStorageKey() {
    return this.storageName + "_fj-rc";
  }
}

export class ValidationError extends Error {
  constructor(
    message: string,
    public readonly errors: string[],
  ) {
    super(message);
    this.name = "ValidationError";
  }
}

export const isDomainError = (e: unknown): e is DomainError =>
  e instanceof DomainError;

export const isApplicationError = (e: unknown): e is ApplicationError =>
  e instanceof ApplicationError;

export const isValidationError = (e: unknown): e is ValidationError =>
  e instanceof ValidationError;

const constructUrl = (base: string, path: string) =>
  base === "/" || base === "" ? path : new URL(path, base).href;
